import * as joint from '@clientio/rappid';

import RouterConfig from './Router.config';
// eslint-disable-next-line @typescript-eslint/no-namespace,

type Point = joint.g.Point;
type Rect = joint.g.Rect;

interface SearchInput {
  sourceAnchor: string;
  targetAnchor: string;
  sourceBbox: Rect;
  targetBbox: Rect;
  sourcePoint: Point;
  targetPoint: Point;
}

export class CustomRouter {
  static router(vertices: joint.g.PlainPoint[], linkView: joint.dia.LinkView) {
    CustomRouter.setCorrectAnchor(linkView);
    return CustomRouter.rightAngleAndCustom(vertices, linkView);
  }

  static rightAngleAndCustom(vertices: joint.g.PlainPoint[], linkView: joint.dia.LinkView) {
    const calculatedVertices = joint.routers.rightAngle(
      vertices,
      {
        targetDirection: joint.routers.rightAngle.Directions.MAGNET_SIDE,
      },
      linkView,
    );

    if (!linkView.model.attributes.manual && CustomRouter.shouldDrawStraight(linkView)) {
      linkView.model.set('vertices', []);
      return [];
    }

    linkView.model.set('vertices', calculatedVertices);

    if (vertices.length === 0) {
      return calculatedVertices;
    }

    if (linkView.model.attributes.manual) {
      const customVertices = CustomRouter.customRouter(vertices, linkView);
      linkView.model.set('vertices', customVertices);
      return customVertices;
    }

    return calculatedVertices;
  }

  static customRouter(vertices: joint.g.PlainPoint[], linkView: joint.dia.LinkView) {
    const sourceAnchor: string = linkView.model.attributes.source.anchor.name;
    const targetAnchor: string = linkView.model.attributes.target.anchor.name;
    const sourceBbox: Rect = linkView.model.getSourceCell().getBBox();
    const targetBbox: Rect = linkView.model.getTargetCell().getBBox();
    const sourcePoint: Point = CustomRouter.getCorrectPoint(sourceBbox, sourceAnchor);
    const targetPoint: Point = CustomRouter.getCorrectPoint(targetBbox, targetAnchor);

    const searchInput: SearchInput = { sourceAnchor, targetAnchor, sourceBbox, targetBbox, sourcePoint, targetPoint };
    if (!linkView.model.attributes.editVertices) {
      CustomRouter.adjustInclinedVertices(vertices, searchInput);
    }
    return vertices;
  }

  static getIrregularMargin(anchor: string): number {
    const margin = RouterConfig.padding;
    if (anchor === 'left') {
      return -margin;
    } else if (anchor === 'right') {
      return margin;
    } else if (anchor === 'top') {
      return -margin;
    } else if (anchor === 'bottom') {
      return margin;
    }
  }

  static checkRedundancy(vertices: joint.g.PlainPoint[], sourcePoint: joint.g.PlainPoint): void {
    for (let i = 0; i < vertices.length - 2; i++) {
      if (i === 0) {
        if (this.isVerticeInBetweenRedundant(sourcePoint, vertices[i], vertices[i + 1])) {
          vertices.splice(i, 1);
          break;
        }
      }
      if (this.isVerticeInBetweenRedundant(vertices[i], vertices[i + 1], vertices[i + 2])) {
        vertices.splice(i + 1, 1);
        break;
      }
    }
  }

  static isVerticeInBetweenRedundant(verticeA: joint.g.PlainPoint, verticeB: joint.g.PlainPoint, verticeC: joint.g.PlainPoint) {
    const isVertical = verticeB.x - verticeA.x === 0 && verticeC.x - verticeA.x === 0;
    const isHorizontal = verticeB.y - verticeA.y === 0 && verticeC.y - verticeA.y === 0;
    if (isHorizontal && (verticeB.y - verticeA.y) / (verticeB.x - verticeA.x) === (verticeC.y - verticeA.y) / (verticeC.x - verticeA.x)) {
      return true;
    }

    if (isVertical && (verticeB.x - verticeA.x) / (verticeB.y - verticeA.y) === (verticeC.x - verticeA.x) / (verticeC.y - verticeA.y)) {
      return true;
    }

    return false;
  }

  // Corrects on node move
  static adjustInclinedVertices(vertices: joint.g.PlainPoint[], searchInput: SearchInput) {
    for (let i = 0; i < vertices.length; i++) {
      if (i === 0) {
        if (!CustomRouter.checkIfPerpendicular(vertices[0], searchInput.sourcePoint)) {
          CustomRouter.keepPerpendicualOnNodeMove(vertices[0], searchInput.sourceAnchor, searchInput.sourcePoint);
        }
      } else {
        if (i === vertices.length - 1) {
          if (!CustomRouter.checkIfPerpendicular(vertices[i], searchInput.targetPoint)) {
            CustomRouter.keepPerpendicualOnNodeMove(vertices[i], searchInput.targetAnchor, searchInput.targetPoint);
          }
        }
      }
    }
  }

  static keepPerpendicualOnNodeMove(vertice: joint.g.PlainPoint, anchor: string, point: joint.g.Point) {
    if (anchor === 'left' || anchor === 'right') {
      vertice.y = point.y;
    } else {
      vertice.x = point.x;
    }
  }

  static checkIfPerpendicular(vertice1: joint.g.PlainPoint, vertice2: joint.g.PlainPoint) {
    return vertice1.x === vertice2.x || vertice1.y === vertice2.y;
  }

  static shouldDrawStraight(linkView: joint.dia.LinkView): boolean {
    const sourceAnchor: string = linkView.model.attributes.source.anchor?.name;
    const targetAnchor: string = linkView.model.attributes.target.anchor?.name;
    const source = linkView.sourceAnchor;
    const target = linkView.targetAnchor;

    if (!source || !target) {
      return false;
    }

    const isVerticalPair =
      (sourceAnchor === 'bottom' && targetAnchor === 'top') || (sourceAnchor === 'top' && targetAnchor === 'bottom');
    const isHorizontalPair =
      (sourceAnchor === 'right' && targetAnchor === 'left') || (sourceAnchor === 'left' && targetAnchor === 'right');

    if (!isVerticalPair && !isHorizontalPair) {
      return false;
    }

    const isAligned = isVerticalPair ? source.x === target.x : source.y === target.y;
    const isForward = isVerticalPair
      ? sourceAnchor === 'bottom'
        ? source.y < target.y
        : source.y > target.y
      : sourceAnchor === 'right'
        ? source.x < target.x
        : source.x > target.x;

    return isForward && isAligned;
  }

  static setSourceAnchor(linkView: joint.dia.LinkView) {
    const anchor = linkView.model.attributes.source.anchor;
    const magnet = linkView.model.attributes.source.magnet;
    if (anchor) {
      const dx = anchor.args?.dx;
      const dy = anchor.args?.dy;
      if (dx && dy) {
        const correctAnchor = CustomRouter.convertPercentageToAnchorPoint(dx, dy);
        linkView.model.source({ id: linkView.model.getSourceCell().attributes.id, anchor: { name: correctAnchor } });
      }
    } else if (magnet) {
      const correctAnchor = CustomRouter.convertMagnetToAnchorPoint(magnet);
      linkView.model.source({ id: linkView.model.getSourceCell().attributes.id, anchor: { name: correctAnchor } });
    }
  }

  static setTargetAnchor(linkView: joint.dia.LinkView) {
    const anchor = linkView.model.attributes.target.anchor;
    const magnet = linkView.model.attributes.target.magnet;
    if (anchor) {
      const dx = anchor.args?.dx;
      const dy = anchor.args?.dy;
      if (dx && dy) {
        const correctAnchor = CustomRouter.convertPercentageToAnchorPoint(dx, dy);
        linkView.model.target({ id: linkView.model.getTargetCell().attributes.id, anchor: { name: correctAnchor } });
      }
    } else if (magnet) {
      const correctAnchor = CustomRouter.convertMagnetToAnchorPoint(magnet);
      linkView.model.target({ id: linkView.model.getTargetCell().attributes.id, anchor: { name: correctAnchor } });
    }
  }

  static convertMagnetToAnchorPoint(magnet) {
    if (magnet === 'portLeft') {
      return 'left';
    } else if (magnet === 'portTop') {
      return 'top';
    } else if (magnet === 'portRight') {
      return 'right';
    } else if (magnet === 'portBottom') {
      return 'bottom';
    }
  }

  static convertPercentageToAnchorPoint(dx, dy) {
    if (dx && dy) {
      if (dx === '0%' && dy === '50%') {
        return 'left';
      } else if (dx === '50%' && dy === '0%') {
        return 'top';
      } else if (dx === '100%' && dy === '50%') {
        return 'right';
      } else if (dx === '50%' && dy === '100%') {
        return 'bottom';
      }
    }
  }

  static setCorrectAnchor(linkView: joint.dia.LinkView) {
    CustomRouter.setSourceAnchor(linkView);
    CustomRouter.setTargetAnchor(linkView);
  }

  static getCorrectPoint(bBox: joint.g.Rect, anchor: string): joint.g.Point {
    switch (anchor) {
      case 'left':
        return bBox.leftMiddle();
      case 'top':
        return bBox.topMiddle();
      case 'right':
        return bBox.rightMiddle();
      case 'bottom':
        return bBox.bottomMiddle();
      default:
        return bBox.bottomMiddle();
    }
  }
}
